<template>
  <div>
    <AddContactModal
      v-if="showAddContactModal"
      @close-add-contact-modal="showAddContactModal = false"
      @refresh-contacts="getContacts()"
    />
    <EditBucketOptionModal
      v-if="showEditBucketOptionModal"
      :selectedBucketOption="selectedBucketOption"
      @close-edit-bucket-option-modal="showEditBucketOptionModal = false"
      @refresh-bucket-options="getBucketOptions()"
    />
    <DeleteConfirmationModal
      v-if="showDeleteConfirmationModal"
      :deleteFunction="deleteBucketOption"
      entity="Bucket"
      @close-delete-confirmation-modal="showDeleteConfirmationModal = false"
      @refresh-records="refreshBucketOptions()"
    />
    <LinkedInImportModal :show="showLinkedInImportModal" @close="showLinkedInImportModal = false" />
    <div class="navbar d-flex justify-content-between align-items-center" :data-route="routeName">
      <div
        v-if="routeName === BUCKETS_ROUTE && currentBucketOption"
        class="d-flex align-items-center"
      >
        <div
          class="bucket-icon-wrapper"
          :style="`background-color: ${currentBucketOption.colorHex || '#3e3efe'};`"
        >
          <Icon icon="fa7-solid:bucket" class="bucket-icon" style="color: white" />
        </div>
        <h6 class="bucket-name">
          {{ currentBucketOption.name }}
        </h6>
        <PopoverMenu
          :items="bucketMenuItems"
          trigger-icon="mdi:dots-horizontal"
          placement="bottom"
          class="bucket-menu-popover"
        />
      </div>

      <div class="navbar-title-wrapper">
        <OwnerFilterHeader :route="routeName" :account="account" />
      </div>
      <GlobalSearch :route="routeName" :account="account" />
      <div
        v-if="!hideForRoutes.includes(routeName)"
        class="d-flex align-items-center ml-auto navbar-right-section"
      >
        <div class="navbar-buttons">
          <div class="navbar-btn-wrapper">
            <button
              type="button"
              class="navbar-agent-btn"
              aria-label="Open AI prospecting agent"
              @click="handleOpenAgent"
            >
              <span class="navbar-agent-btn__icon-wrap">
                <Icon icon="mdi:account-circle" class="navbar-agent-btn__icon" />
                <Icon icon="mdi:creation" class="navbar-agent-btn__sparkle" />
                <span class="navbar-agent-btn__plus">
                  <Icon icon="mdi:plus" />
                </span>
              </span>
              <span class="navbar-btn-label">AI Prospector</span>
            </button>
            <div class="navbar-btn-tooltip">AI Prospector</div>
          </div>
          <div class="navbar-btn-wrapper">
            <Button
              variant="secondary"
              size="sm"
              icon="lucide:linkedin"
              @click="handleOpenLinkedInImport"
            >
              <span class="navbar-btn-label">LinkedIn Import</span>
            </Button>
            <div class="navbar-btn-tooltip">LinkedIn Import</div>
          </div>
          <div class="navbar-btn-wrapper">
            <Button
              variant="secondary"
              size="sm"
              icon="lucide:file-up"
              @click="handleOpenImportContact"
            >
              <span class="navbar-btn-label">Import Contact</span>
            </Button>
            <div class="navbar-btn-tooltip">Import Contact</div>
          </div>
          <div class="navbar-btn-wrapper">
            <Button
              variant="primary"
              size="sm"
              icon="lucide:user-plus"
              @click="handleOpenAddContact"
            >
              <span class="navbar-btn-label">New Contact</span>
            </Button>
            <div class="navbar-btn-tooltip">New Contact</div>
          </div>
        </div>

        <Dialer :route="routeName" :account="account" :hide-for-routes="hideForRoutes" />

        <NotificationBell :route="routeName" :account="account" />

        <div class="profile-image-wrapper" style="margin: 0px 0px 0px 6px">
          <img v-if="currentImage" :src="currentImage" class="profile-image-circle" alt="Profile" />
          <div v-else class="profile-image-circle profile-image-circle--fallback">
            <span>{{ account?.fullName?.charAt(0)?.toUpperCase() }}</span>
          </div>
        </div>

        <div class="profile-dropdown" ref="profileDropdownRef">
          <div
            class="avatar"
            type="button"
            :aria-expanded="profileMenuOpen"
            aria-haspopup="menu"
            @click="toggleProfileMenu"
          >
            <Icon icon="mdi:chevron-down" width="14" height="14" />
          </div>
          <transition name="profile-menu">
            <div
              v-if="profileMenuOpen"
              class="profile-menu"
              role="menu"
              @click.stop
              @keydown.esc="profileMenuOpen = false"
            >
              <div class="dropdown-user-info">
                <div class="dropdown-user-avatar">
                  <img v-if="currentImage" :src="currentImage" alt="Profile" />
                  <span v-else>{{ account?.fullName?.charAt(0)?.toUpperCase() }}</span>
                </div>
                <div class="dropdown-user-details">
                  <div class="dropdown-user-name">{{ account?.fullName || "User" }}</div>
                  <div class="dropdown-user-email">{{ account?.email || "" }}</div>
                </div>
              </div>

              <ProfileDropdownTrialSection @navigate="profileMenuOpen = false" />

              <div class="dropdown-nav">
                <router-link
                  :to="{ name: SETTINGS_PROFILE_ROUTE }"
                  class="dropdown-item"
                  role="menuitem"
                  @click="profileMenuOpen = false"
                >
                  <Icon icon="mdi:cog-outline" width="16" height="16" />
                  <span>Settings</span>
                </router-link>

                <a
                  href="https://chromewebstore.google.com/detail/salesbucket-chrome-extens/gnfkjdffjdjfaeldajkkhomcfdajiifn?utm_source=item-share-cb"
                  class="dropdown-item"
                  role="menuitem"
                  target="_blank"
                  rel="noopener"
                  @click="profileMenuOpen = false"
                >
                  <Icon icon="mdi:puzzle-outline" width="16" height="16" />
                  <span>Chrome Extension</span>
                  <Icon
                    icon="mdi:open-in-new"
                    width="12"
                    height="12"
                    class="dropdown-item__external"
                  />
                </a>

                <a
                  class="dropdown-item"
                  role="menuitem"
                  @click="
                    openTutorial();
                    profileMenuOpen = false;
                  "
                >
                  <Icon icon="mdi:help-circle-outline" width="16" height="16" />
                  <span>Help & Support</span>
                </a>

                <div class="dropdown-divider"></div>

                <a
                  class="dropdown-item dropdown-item--danger"
                  role="menuitem"
                  @click="
                    logout();
                    profileMenuOpen = false;
                  "
                >
                  <Icon icon="mdi:logout" width="16" height="16" />
                  <span>Sign out</span>
                </a>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import EditBucketOptionModal from "@/components/bucket/options/EditBucketOptionModal.vue";
import AddContactModal from "@/components/contacts/AddContactModal.vue";
import LinkedInImportModal from "@/components/contacts/linkedin-import/LinkedInImportModal.vue";
import NotificationBell from "@/components/notification/NotificationBell.vue";
import OwnerFilterHeader from "@/components/navbar/OwnerFilterHeader.vue";
import GlobalSearch from "@/components/navbar/GlobalSearch.vue";
import Dialer from "@/components/navbar/Dialer.vue";
import ProfileDropdownTrialSection from "@/components/navbar/ProfileDropdownTrialSection.vue";
import { Button } from "@/components/ui";

import {
  AI_AGENT_ROUTE,
  BUCKETS_LIST_ROUTE,
  BUCKETS_ROUTE,
  COMPANIES_ROUTE,
  CONTACT_IMPORT_ROUTE,
  LOGIN_ROUTE,
  SETTINGS_PROFILE_ROUTE,
  SUBSCRIPTION_SUCCESS_ROUTE,
  SUBSCRIPTION_WALL_ROUTE,
  SUPPORT_ROUTE,
} from "@/router/routes";
import { GET_BUCKET_OPTIONS, GET_BUCKETS, GET_CONTACTS, LOGOUT } from "@/store/action-types";
import { STORAGE_URL, USER_PROFILE_PIC_DIR } from "@/utils/const";
import { useDeleteBucketOption } from "@/composables/bucket/useBucketOption";
import { useToast } from "@/composables/ui/useToast";

import { Icon, PopoverMenu } from "@/components/ui";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { mutateAsync: deleteBucketOptionMutation } = useDeleteBucketOption();
const $toast = useToast();

// State
const showAddContactModal = ref(false);
const showEditBucketOptionModal = ref(false);
const showDeleteConfirmationModal = ref(false);
const showLinkedInImportModal = ref(false);
const currentImage = ref(null);
const profileMenuOpen = ref(false);
const profileDropdownRef = ref(null);

function toggleProfileMenu() {
  profileMenuOpen.value = !profileMenuOpen.value;
}

function handleProfileOutsideClick(event) {
  if (!profileMenuOpen.value) return;
  const root = profileDropdownRef.value;
  if (root && !root.contains(event.target)) {
    profileMenuOpen.value = false;
  }
}

function handleProfileEscape(event) {
  if (event.key === "Escape" && profileMenuOpen.value) {
    profileMenuOpen.value = false;
  }
}

watch(
  () => route.fullPath,
  () => {
    profileMenuOpen.value = false;
  }
);
const selectedBucketOption = ref(null);
const currentPage = ref(1);
const perPage = ref(10);
const selectedDate = ref(null);
const selectedTab = ref(null);

const hideForRoutes = [SUBSCRIPTION_WALL_ROUTE, SUBSCRIPTION_SUCCESS_ROUTE];

// Computed
const routeName = computed(() => route.name);
const bucketOptions = computed(() => store.getters.bucketOptions.data);
const account = computed(() => store.getters.account.data);
const currentBucketOption = computed(() => {
  if (routeName.value !== BUCKETS_ROUTE || !bucketOptions.value?.length) {
    return null;
  }
  return bucketOptions.value.find((e) => e.slug == route.params.bucket) || null;
});

const bucketMenuItems = computed(() => {
  if (!currentBucketOption.value) {
    return [];
  }

  return [
    {
      label: "Edit Bucket",
      icon: "mdi:pencil-outline",
      onClick: () => selectBucketToEdit(currentBucketOption.value),
    },
    {
      label: "Delete",
      icon: "mdi:delete-outline",
      onClick: () => toggleDeleteConfirmationModal(currentBucketOption.value),
      danger: true,
    },
  ];
});

// Methods
const openTutorial = () => {
  const routeResolved = router.resolve({ name: SUPPORT_ROUTE });
  window.open(routeResolved.href, "_blank");
};

const setValues = () => {
  if (account.value?.profilePic) {
    const url = `${STORAGE_URL}/${USER_PROFILE_PIC_DIR}/${account.value.profilePic}`;
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch profile picture");
        }
        return response.blob();
      })
      .then((blob) => {
        currentImage.value = URL.createObjectURL(blob);
      })
      .catch((error) => {
        console.error("Error fetching profile picture:", error);
      });
  }
};

const logout = () => {
  store.dispatch(LOGOUT).then(() => {
    router.push({ name: LOGIN_ROUTE });
  });
};

const selectBucketToEdit = (bucketOption) => {
  selectedBucketOption.value = bucketOption;
  showEditBucketOptionModal.value = true;
};

const toggleDeleteConfirmationModal = (bucketOption) => {
  selectedBucketOption.value = bucketOption;
  showDeleteConfirmationModal.value = true;
};

const deleteBucketOption = async () => {
  try {
    await deleteBucketOptionMutation(selectedBucketOption.value.id);
    selectedBucketOption.value = null;
    return true;
  } catch (error) {
    console.error("Delete error:", error);
    $toast.error("Failed to delete bucket.");
    return false;
  }
};

const getContacts = () => {
  const params = {
    page: currentPage.value,
    limit: perPage.value,
    organizationId: account.value.organizationId,
    searchTerm: null,
  };

  if (route.query.owner && route.query.owner != "All") {
    params.createdById = route.query.owner;
  }

  store.dispatch(GET_CONTACTS, params).then((res) => {
    if (res) {
      selectedDate.value = null;
    }
  });
};

const getBuckets = () => {
  store
    .dispatch(GET_BUCKETS, {
      bucket: route.params.bucket,
      followUp: selectedTab.value ?? route.query.status,
      organizationId: account.value.organizationId,
    })
    .then((res) => {
      if (res) {
        selectedDate.value = null;
      }
    });
};

const refreshBucketOptions = () => {
  router.push({ name: BUCKETS_LIST_ROUTE });
  store.dispatch(GET_BUCKET_OPTIONS, {
    organizationId: account.value.organizationId,
  });
};

const getBucketOptions = () => {
  store.dispatch(GET_BUCKET_OPTIONS, {
    organizationId: account.value.organizationId,
  });
  getBuckets();
};

const handleOpenLinkedInImport = () => {
  showLinkedInImportModal.value = true;
};

const handleOpenImportContact = () => {
  router.push({ name: CONTACT_IMPORT_ROUTE });
};

const handleOpenAddContact = () => {
  showAddContactModal.value = true;
};

const handleOpenAgent = () => {
  router.push({ name: AI_AGENT_ROUTE });
};

onMounted(() => {
  setValues();
  if (account.value?.organizationId) {
    store.dispatch(GET_BUCKET_OPTIONS, {
      organizationId: account.value.organizationId,
    });
  }
  document.addEventListener("mousedown", handleProfileOutsideClick);
  document.addEventListener("keydown", handleProfileEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleProfileOutsideClick);
  document.removeEventListener("keydown", handleProfileEscape);
});
</script>
<style scoped>
.bucket-icon-wrapper {
  margin-left: 4px;
  margin-right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bucket-icon {
  width: 16px;
  height: 16px;
}

.bucket-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.bucket-menu-popover {
  margin-left: 8px;
}

.bucket-menu-popover :deep(.popover-menu__trigger) {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: white;
  box-shadow: 0px 1.25px 3.13px rgba(38, 51, 77, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.bucket-menu-popover :deep(.popover-menu__trigger:hover) {
  background-color: #f3f4f6;
}

.navbar {
  padding-left: 16px;
  padding-right: 16px;
  background-color: #ffffff;
  border-bottom: 1px solid #e4e4e7;
  min-height: 64px;
}

.navbar .btn-unfilled,
.navbar .btn-filled {
  height: 32px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.navbar-buttons {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}

.navbar-title-wrapper {
  min-width: 0;
  display: flex;
  align-items: center;
  flex-shrink: 1;
  overflow: visible;
}

.navbar-buttons :deep(.ui-button) {
  height: 32px;
  font-size: 0.8rem;
}

.navbar-right-section {
  gap: 4px;
  flex-wrap: nowrap;
  min-width: 0;
}

.profile-dropdown {
  position: relative;
  margin-right: 0;
}

.profile-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1050;
  min-width: 280px;
  max-width: min(280px, calc(100vw - 16px));
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow:
    0 8px 24px rgba(17, 24, 39, 0.12),
    0 2px 4px rgba(17, 24, 39, 0.05);
  padding: 0;
}

.profile-menu-enter-active,
.profile-menu-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  transform-origin: top right;
}

.profile-menu-enter-from,
.profile-menu-leave-to {
  opacity: 0;
  transform: scale(0.98) translateY(-4px);
}

.profile-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-image-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e4e4e7;
  background: #fff;
  transition:
    box-shadow 0.2s ease,
    transform 0.1s ease;
}

.profile-image-circle:hover {
  box-shadow: 0px 2px 8px rgba(62, 62, 254, 0.2);
  transform: scale(1.05);
  cursor: pointer;
}

.profile-image-circle--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3e3efe 0%, #6366f1 100%);
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1;
  text-transform: uppercase;
}

.dropdown-user-info {
  padding: 12px;
  background: #fafafa;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e5e7eb;
}

.dropdown-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, #3e3efe 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
}

.dropdown-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dropdown-user-details {
  flex: 1;
  min-width: 0;
}

.dropdown-user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.2;
}

.dropdown-user-email {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-nav {
  padding: 6px;
}

.profile-menu .dropdown-divider {
  margin: 4px 0;
  border-top: 1px solid #e5e7eb;
}

.profile-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
}

.profile-menu .dropdown-item:hover,
.profile-menu .dropdown-item:active,
.profile-menu .dropdown-item:focus-visible {
  background-color: #f3f4f6;
  color: #111827;
  outline: none;
}

.profile-menu .dropdown-item .iconify {
  color: #6b7280;
  flex-shrink: 0;
}

.profile-menu .dropdown-item:hover .iconify {
  color: #374151;
}

.dropdown-item__external {
  margin-left: auto;
  opacity: 0.5;
}

.dropdown-item--danger {
  color: #dc2626;
}

.dropdown-item--danger .iconify {
  color: #dc2626;
}

.dropdown-item--danger:hover {
  background: #fef2f2;
  color: #b91c1c;
}

.dropdown-item--danger:hover .iconify {
  color: #b91c1c;
}

.avatar {
  width: 1.4rem;
  height: 1.4rem;
  color: #18181b;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.95em;
  font-weight: bold;
  margin: 0 8px 0 6px;
  cursor: pointer;
}

.navbar-agent-btn {
  --agent-btn-bg: var(--color-bg-white);
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px 0 8px;
  border: 1px solid var(--color-primary);
  border-radius: calc(var(--radius) - 2px);
  background-color: transparent;
  color: var(--color-primary);
  font-family: var(--font-family);
  font-size: var(--font-size-xs);
  font-weight: 400;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-normal);
}

.navbar-agent-btn:hover {
  --agent-btn-bg: var(--color-primary);
  background-color: var(--color-primary);
  color: var(--color-bg-white);
}

.navbar-agent-btn__icon-wrap {
  position: relative;
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
}

.navbar-agent-btn__icon {
  width: 20px;
  height: 20px;
}

.navbar-agent-btn__sparkle {
  position: absolute;
  top: -1px;
  right: -1px;
  width: 11px;
  height: 11px;
  background-color: var(--agent-btn-bg);
  border-radius: 50%;
  transition: background-color var(--transition-normal);
}

.navbar-agent-btn__plus {
  position: absolute;
  right: -1px;
  bottom: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border: 1.5px solid var(--agent-btn-bg);
  border-radius: 50%;
  background: var(--color-primary);
  color: var(--color-bg-white);
  font-size: 8px;
  transition: all var(--transition-normal);
}

.navbar-agent-btn:hover .navbar-agent-btn__plus {
  background: var(--color-bg-white);
  color: var(--color-primary);
}

.navbar-btn-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.navbar-btn-tooltip {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow:
    0 8px 24px rgba(17, 24, 39, 0.12),
    0 2px 4px rgba(17, 24, 39, 0.05);
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  z-index: 1050;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  display: none;
}

@media (max-width: 1100px) {
  .navbar {
    padding-left: 10px;
    padding-right: 10px;
  }

  .navbar-buttons {
    gap: 4px;
  }

  .navbar-btn-label {
    display: none;
  }

  .navbar-agent-btn {
    width: 36px;
    padding: 0;
  }

  .navbar-right-section {
    gap: 2px;
  }

  .navbar-btn-tooltip {
    display: block;
  }

  .navbar-btn-wrapper:hover .navbar-btn-tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  .navbar-buttons :deep(.ui-button .iconify) {
    width: 16px;
    height: 16px;
  }

  .navbar-buttons :deep(.ui-button) {
    width: 34px;
    padding-left: 0;
    padding-right: 0;
    gap: 0;
    justify-content: center;
  }

  .navbar-buttons :deep(.ui-button__icon--left) {
    margin-right: 0;
  }
}
</style>
